// ═══ Audio Reactivity — Subscribable audio data for any canvas component ═══
// Any component inside the composition can call useAudioReactivity() to get
// per-frame frequency bands, volume, and the raw spectrum.
//
// Usage:
//   const { bass, mid, high, volume, spectrum } = useAudioReactivity()

import React, { createContext, useContext, useEffect, useState } from 'react'
import { useCurrentFrame, useVideoConfig } from 'remotion'
import { getAudioData, visualizeAudio } from '@remotion/media-utils'

// ═══ Types ═══

export interface AudioReactivityData {
  /** Low frequencies (sub-bass + bass, ~20-250Hz). Drives kicks, pulses. 0-1. */
  bass: number
  /** Mid frequencies (~250-2000Hz). Drives vocals, melodies. 0-1. */
  mid: number
  /** High frequencies (~2000-8000Hz). Drives hi-hats, cymbals, sibilance. 0-1. */
  high: number
  /** Overall volume (average of all bands). 0-1. */
  volume: number
  /** Raw FFT spectrum array. 64 bins, each 0-1. For custom visualizations. */
  spectrum: number[]
}

const EMPTY: AudioReactivityData = { bass: 0, mid: 0, high: 0, volume: 0, spectrum: [] }

// ═══ Context ═══

const AudioReactivityContext = createContext<AudioReactivityData>(EMPTY)

/** Read audio reactivity data from the nearest provider. Works in any component. */
export function useAudioReactivity(): AudioReactivityData {
  return useContext(AudioReactivityContext)
}

// ═══ Provider ═══

type AudioDataResult = Awaited<ReturnType<typeof getAudioData>>

export const AudioReactivityProvider: React.FC<{
  audioUrl: string | undefined
  children: React.ReactNode
}> = ({ audioUrl, children }) => {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const [audioData, setAudioData] = useState<AudioDataResult | null>(null)

  useEffect(() => {
    if (!audioUrl) return
    getAudioData(audioUrl).then(setAudioData).catch(() => {})
  }, [audioUrl])

  let value: AudioReactivityData = EMPTY

  if (audioData) {
    const spectrum = visualizeAudio({
      fps,
      frame,
      audioData,
      numberOfSamples: 64,
    })

    // Split spectrum into perceptual frequency bands
    const bass = spectrum.slice(0, 4).reduce((a, b) => a + b, 0) / 4    // Bins 0-3
    const mid = spectrum.slice(4, 16).reduce((a, b) => a + b, 0) / 12   // Bins 4-15
    const high = spectrum.slice(16, 32).reduce((a, b) => a + b, 0) / 16 // Bins 16-31
    const volume = spectrum.reduce((a, b) => a + b, 0) / spectrum.length

    value = { bass, mid, high, volume, spectrum }
  }

  return (
    <AudioReactivityContext.Provider value={value}>
      {children}
    </AudioReactivityContext.Provider>
  )
}
